import type {
  DsdEditorSpellcheckBlock,
  DsdEditorSpellcheckRange,
} from "@/apis/dsd-editor/route";
import type { PlainDsdTable } from "@/types/plain-dsd-table";

export type DsdEditorSpellcheckParsedItem =
  | {
      id: string;
      kind: "table";
      sectionName: string;
      chunkId: string;
      table: PlainDsdTable;
    }
  | {
      id: string;
      kind: "paragraph";
      sectionName: string;
      chunkId: string;
      subChunkIndex: number;
      paragraphIndex: number;
      text: string;
      /** 문단 요소의 data-value-item-ref — 교정 blockId와 같은 체계 */
      valueItemId?: string;
    };

export interface DsdEditorCorrectionItem {
  id: string;
  kind: "paragraph" | "cell";
  sectionName: string;
  block: DsdEditorSpellcheckBlock;
}

export type CorrectionCategory = "spelling" | "spacing";

const KNOWN_CHANGE_TYPES = new Set(["spelling", "spacing", "mixed"]);

// 맞춤법/띄어쓰기는 파티션(항목당 한 카테고리): spelling·mixed 변경이 하나라도
// 있으면 맞춤법, 나머지(띄어쓰기 변경만)는 띄어쓰기. 칩 라벨·탭·개요 카운트가
// 같은 기준을 써서 맞춤법 + 띄어쓰기 = 전체가 항상 성립한다.
//
// 계약 위반은 throw한다 — 조용히 분류하면 깨진 계약 정보 자체가 묻힌다.
// - 알 수 없는 changeType: 새 분류가 기존 탭에 섞여 나가는 것은 "덜 만든
//   기능"의 배포 사고. 백엔드에 changeType이 추가되면 프론트(탭·칩·카운트)도
//   반드시 함께 수정해야 한다.
// - 빈 changes: 교정 항목으로 왔다는 것 자체가 "고칠 내용이 있다"는 계약.
//   파서(route.ts)가 결손 필드를 []로 흡수하므로, 빈 채로 오면 데이터 유실이다.
// throw는 데이터 훅(use-review-document-data)에서 리뷰 실패 UX로 승격된다.
export const getCorrectionCategory = (
  block: DsdEditorSpellcheckBlock,
): CorrectionCategory => {
  if (block.changes.length === 0) {
    throw new Error(
      `교정 항목에 변경 내역(changes)이 없습니다. (blockId: ${block.blockId})`,
    );
  }
  for (const change of block.changes) {
    if (!KNOWN_CHANGE_TYPES.has(change.changeType)) {
      throw new Error(
        `알 수 없는 changeType "${change.changeType}" (blockId: ${block.blockId}) — ` +
          "새 분류는 프론트 탭/칩/카운트 구현과 함께 추가해야 합니다.",
      );
    }
  }
  return block.changes.some(
    (change) =>
      change.changeType === "spelling" || change.changeType === "mixed",
  )
    ? "spelling"
    : "spacing";
};

/** 맞춤법·띄어쓰기 항목 수. 한 항목이라도 계약을 어기면 getCorrectionCategory 처럼 throw 한다 */
export const countCorrectionCategories = (
  items: readonly DsdEditorCorrectionItem[],
): Record<CorrectionCategory, number> => {
  const counts = { spelling: 0, spacing: 0 };
  for (const item of items) counts[getCorrectionCategory(item.block)] += 1;
  return counts;
};

export interface SpellcheckDiffSegment {
  text: string;
  changed: boolean;
}

export const isSpellcheckTableBlock = (block: DsdEditorSpellcheckBlock) =>
  block.tableIndex !== null &&
  block.tableIndex !== undefined &&
  block.row !== null &&
  block.row !== undefined &&
  block.col !== null &&
  block.col !== undefined;

// 계약: spellcheck blockId == 렌더된 요소의 id/data-value-item-ref.
// (백엔드 value_item_ref() 단일 직렬화 — 셀·문단 동일) 좌표·텍스트로 "복구"하면
// 다른 요소에 붙으므로, 매칭은 blockId 직접 대조만 하고 못 찾으면 버린다.
export const getSpellcheckBlockByItemId = (
  items: DsdEditorSpellcheckParsedItem[],
  blocks: DsdEditorSpellcheckBlock[],
) => {
  const map = new Map<string, DsdEditorSpellcheckBlock>();
  const usedBlocks = new Set<DsdEditorSpellcheckBlock>();
  const paragraphBlockById = new Map(
    blocks
      .filter((block) => !isSpellcheckTableBlock(block))
      .map((block) => [block.blockId, block] as const),
  );

  for (const item of items) {
    if (item.kind !== "paragraph" || !item.valueItemId) continue;

    const block = paragraphBlockById.get(item.valueItemId);
    if (block && !usedBlocks.has(block)) {
      map.set(item.id, block);
      usedBlocks.add(block);
    }
  }

  return map;
};

export const getSpellcheckBlockByCellId = (
  blocks: DsdEditorSpellcheckBlock[],
  renderedCellIds: ReadonlySet<string>,
) => {
  const map = new Map<string, DsdEditorSpellcheckBlock>();

  for (const block of blocks) {
    if (!isSpellcheckTableBlock(block)) continue;
    if (!renderedCellIds.has(block.blockId) || map.has(block.blockId)) continue;

    map.set(block.blockId, block);
  }

  return map;
};

export const getCorrectionItems = (
  items: DsdEditorSpellcheckParsedItem[],
  blockByItemId: ReadonlyMap<string, DsdEditorSpellcheckBlock>,
  blockByCellId: ReadonlyMap<string, DsdEditorSpellcheckBlock>,
) => {
  const correctionItems: DsdEditorCorrectionItem[] = [];

  for (const item of items) {
    if (item.kind === "paragraph") {
      const block = blockByItemId.get(item.id);
      if (block) {
        correctionItems.push({
          id: item.id,
          kind: "paragraph",
          sectionName: item.sectionName,
          block,
        });
      }
      continue;
    }

    for (const row of item.table.rows) {
      for (const cell of row.cells) {
        const block = blockByCellId.get(cell.cellId);
        if (!block) continue;

        correctionItems.push({
          id: cell.cellId,
          kind: "cell",
          sectionName: item.sectionName,
          block,
        });
      }
    }
  }

  return correctionItems;
};

export const isVisibleWhitespace = (char: string) =>
  char === " " || char === "\t";

// 백엔드가 준 변경 구간(정렬·비중첩, UTF-16 코드유닛)으로 텍스트를
// changed/unchanged 세그먼트로 분할. diff 계산은 전부 백엔드 책임.
export const buildSpellcheckRangeSegments = (
  text: string,
  ranges: DsdEditorSpellcheckRange[],
): SpellcheckDiffSegment[] => {
  const segments: SpellcheckDiffSegment[] = [];
  let cursor = 0;

  for (const range of ranges) {
    // length 0 = 반대편 텍스트에만 있는 변경(삽입/삭제 지점) — 칠할 구간 없음
    if (range.length <= 0) continue;

    const start = Math.min(Math.max(range.offset, cursor), text.length);
    const end = Math.min(range.offset + range.length, text.length);
    if (end <= start) continue;

    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), changed: false });
    }
    segments.push({ text: text.slice(start, end), changed: true });
    cursor = end;
  }

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), changed: false });
  }

  return segments;
};

export const getSpellcheckOriginalSegments = (
  block: DsdEditorSpellcheckBlock,
): SpellcheckDiffSegment[] =>
  buildSpellcheckRangeSegments(
    block.originalText,
    block.changes.map((change) => change.originalRange),
  );

// 교정문은 "추가된 글자"만 칠한다 (어절 range가 아닌 글자 run)
export const getSpellcheckCorrectedSegments = (
  block: DsdEditorSpellcheckBlock,
): SpellcheckDiffSegment[] =>
  buildSpellcheckRangeSegments(
    block.correctedText,
    block.changes.flatMap((change) => change.correctedRuns),
  );

/** range 구간을 잘라낸 발췌 텍스트 (변경 사항 목록용) */
export const getSpellcheckRangeText = (
  text: string,
  range: DsdEditorSpellcheckRange,
) => text.slice(range.offset, range.offset + range.length);

/** 어절 발췌 안에서 글자 run만 칠한 세그먼트 (변경 사항 목록용). run은 절대 offset → 발췌 상대로 변환 */
export const buildSpellcheckExcerptSegments = (
  text: string,
  range: DsdEditorSpellcheckRange,
  runs: DsdEditorSpellcheckRange[],
): SpellcheckDiffSegment[] =>
  buildSpellcheckRangeSegments(
    getSpellcheckRangeText(text, range),
    runs.map((run) => ({
      offset: run.offset - range.offset,
      length: run.length,
    })),
  );
